import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { sortBy } from "lodash";

import { useAttempts } from "../../../hooks/useAttempts";
import useCategorySelection from "../../../hooks/useCategorySelection";
import useCompetition, {
  useCompetitionEntries,
  useCompetitionRouteJudgePermissions,
} from "../../../hooks/useCompetition";
import useDebounce from "../../../hooks/useDebounce";
import {
  addToQueue,
  putQueue,
  useRouteQueueUserUUIDs,
} from "../../../hooks/useRouteQueue";
import { useRoute } from "../../../hooks/useVenueRoutes";
import { useJudgeAuthWall } from "../../../modules/onboarding/components/hooks/useAuthWall";
import useCurrentUser from "../../../hooks/useCurrentUser";
import useLocalStorage from "../../../hooks/useLocalStorage";

export type SortOption =
  | "alpha-asc"
  | "alpha-desc"
  | "entry-asc"
  | "entry-desc";

export default function useRouteScreen() {
  const { routeUUID, competitionUUID } = useParams();

  const { profile } = useCurrentUser();

  const { competition } = useCompetition(competitionUUID);

  const { route } = useRoute({
    venueUUID: competition?.venues?.[0]?.uuid,
    routeUUID,
  });

  const judgePermissions = useCompetitionRouteJudgePermissions({
    competition,
    routeUUID,
    judgeUUID: profile?.uuid,
  });

  useJudgeAuthWall();

  const {
    selectedCategoryUUIDs,
    selectedCategories,
    availableCategories,
    availableCategoryUUIDs,
    setSelectedCategoryUUIDs,
  } = useCategorySelection({ competition, route });
  const { entries } = useCompetitionEntries(competitionUUID);
  const { queue } = useRouteQueueUserUUIDs({ competitionUUID, routeUUID });

  const updateQueue = (updatedQueue: string[]) =>
    routeUUID && competitionUUID
      ? putQueue({ routeUUID, competitionUUID, queue: updatedQueue })
      : undefined;

  const [sortOption, setSortOption] = useLocalStorage<SortOption>(
    "routeEveryoneListSort",
    "alpha-asc"
  );

  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 100);

  const { attemptsByUser } = useAttempts(routeUUID);
  const entriesWithData = useMemo(() => {
    return entries
      ? entries.map((e) => ({
          ...e,
          attempts: (attemptsByUser && attemptsByUser[e.user.uuid]) || [],
          queueIndex: queue?.indexOf(e.user.uuid),
        }))
      : [];
  }, [entries, attemptsByUser, queue]);

  const sortedEntries = useMemo(
    () =>
      sortBy(entriesWithData, (x) => [
        x.queueIndex === -1 ? "Z" : x.queueIndex,
        x.queueIndex === -1 && x.attempts.find((y) => y.success) ? "Z" : "A",
        x.user.firstName,
      ]),
    [entriesWithData]
  );

  const entriesWithMatch = useMemo(() => {
    return (
      sortedEntries?.map((entry) => ({
        ...entry,
        matchesFilter:
          !!entry.category?.uuid &&
          (selectedCategories.length
            ? !!selectedCategories.find(
                (cat) => cat.uuid === entry.category?.uuid
              )
            : availableCategoryUUIDs.includes(entry.category?.uuid)) &&
          (entry.user.firstName
            .toLowerCase()
            .startsWith(debouncedSearchText.toLowerCase()) ||
            entry.user.lastName
              .toLowerCase()
              .startsWith(debouncedSearchText.toLowerCase()) ||
            (entry.user.firstName + " " + entry.user.lastName)
              .toLowerCase()
              .startsWith(debouncedSearchText.toLowerCase())),
      })) || []
    );
  }, [
    sortedEntries,
    debouncedSearchText,
    availableCategoryUUIDs,
    selectedCategories,
  ]);

  const queuedEntries = useMemo(
    () => entriesWithMatch.filter((x) => x.queueIndex !== -1),
    [entriesWithMatch]
  );
  const unqueuedEntries = useMemo(() => {
    const filtered = entriesWithMatch.filter(
      (x) => x.queueIndex === -1 && x.matchesFilter
    );
    switch (sortOption) {
      case "alpha-asc":
        return sortBy(filtered, (x) =>
          (x.user.firstName + " " + x.user.lastName).toLowerCase()
        );
      case "alpha-desc":
        return sortBy(filtered, (x) =>
          (x.user.firstName + " " + x.user.lastName).toLowerCase()
        ).reverse();
      case "entry-asc":
        return sortBy(filtered, (x) => x.creationTime);
      case "entry-desc":
        return sortBy(filtered, (x) => x.creationTime).reverse();
      default:
        return filtered;
    }
  }, [entriesWithMatch, sortOption]);

  const [isDragging, setIsDragging] = useState(false);

  const addEntryToQueue = (userUUID: string) =>
    routeUUID &&
    competitionUUID &&
    addToQueue({ routeUUID, competitionUUID, userUUID });

  return {
    route,
    competition,
    judgePermissions,
    entries: sortedEntries,
    queuedEntries,
    unqueuedEntries,
    setSelectedCategoryUUIDs,
    selectedCategoryUUIDs,
    selectedCategories,
    availableCategories,
    searchText,
    setSearchText,
    sortOption,
    setSortOption,
    addToQueue: addEntryToQueue,
    updateQueue,
    setIsDragging,
    isDragging,
    queue,
  };
}
